import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Alert } from '../../utils/Alert';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../components/layout/MainLayout';
import EntranceLayout from '../../components/layout/EntranceLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { API_URL } from '../../config';
import { renderMathHtml } from '../../utils/renderMath';
// StudentSidebar removed (unused)
// Types
interface ApiOption {
  id: number;
  content: string;
  is_correct: boolean;
  image_url: string | null;
  question_id: number;
  created_at: string;
  updated_at: string;
}

// Removed unused interfaces ApiQuestion and Option

interface Question {
  question_id: number;
  order?: number;
  type: string;
  content: string;
  image_url: string | null;
  options: ApiOption[];
  marks?: number;
  difficulty_level: string;
  topic_id: number | null;
  chapter_id: number | null;
  subject_id: number | null;
  course_id: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  creator: {
    email: string;
    username: string;
    role: string;
    id: number;
    full_name: string;
    created_at: string;
    updated_at: string;
    last_login: string;
  };
  topic: any | null;
  chapter: any | null;
  subject: any | null;
  course: {
    id: number;
    name: string;
    description: string;
  };
  note?: string;
  note_url?: string;
}

interface ExamInfo {
  id: number;
  title: string;
  description: string;
  duration_minutes: number;
  max_marks: number;
  max_questions: number;
}

interface ThreeColumnMatchAnswer {
  matches: { a: number; b: number | ''; c: number | '' }[];
}

interface Answer {
  questionId: number;
  selectedOptionId?: number; // for single correct
  selectedOptionIds?: number[]; // for multiple correct
  enteredValue?: string; // for integer/numerical
  isCorrect?: boolean;
  matchingAnswers?: ThreeColumnMatchAnswer; // for three_column_match
}

// Removed unused interface SubmitAnswerData

interface DashboardPageProps {
  user: {
    name: string;
    role: string;
    avatar?: string;
  };
  onLogout: () => void;
}

const ExamTaking: React.FC<DashboardPageProps> = ({ user, onLogout }) => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Capture returnTo (origin) if provided by navigation state
  const returnTo = location.state?.returnTo;


  // Helper to get initial studentExamId and examData from localStorage or location.state
  const getInitialExamState = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const queryMode = searchParams.get('mode');

    // Check if this is a topic exam from location.state
    if (location.state?.isTopicExam && location.state?.examData) {
      return {
        initialStudentExamId:
          location.state.examMode === 'general' ? null : location.state.topicExamId || null,
        initialExamData: location.state.examData || null,
        isTopicExam: true,
        initialExamMode: location.state.examMode || queryMode || 'standard',
        initialTopicExamId: location.state.topicExamId || null,
        initialGeneralMeta:
          location.state.generalMeta || {
            studentId: location.state.studentId,
            courseId: location.state.courseId,
            attemptNumber: location.state.validationData?.attempt_number || 1,
          },
      };
    }

    if (examId) {
      const savedExamDataStr = localStorage.getItem(`exam_${examId}_data`);
      if (savedExamDataStr) {
        try {
          const parsedData = JSON.parse(savedExamDataStr);
          return {
            initialStudentExamId: parsedData.studentExamId || null,
            initialExamData: parsedData.examData || null,
            isTopicExam: parsedData.isTopicExam || false,
            initialExamMode: parsedData.examMode || queryMode || 'standard',
            initialTopicExamId: parsedData.topicExamId || null,
            initialGeneralMeta: parsedData.generalMeta || null,
          };
        } catch (e) {
          console.error("Error parsing saved exam data from localStorage", e);
          localStorage.removeItem(`exam_${examId}_data`); // Clear corrupted data
        }
      }
    }
    // Fallback to location.state for initial load (when navigating from /student/exams)
    return {
      initialStudentExamId: location.state?.studentExamId || null,
      initialExamData: location.state?.examData || null,
      isTopicExam: location.state?.isTopicExam || false,
      initialExamMode: location.state?.examMode || queryMode || 'standard',
      initialTopicExamId: location.state?.topicExamId || null,
      initialGeneralMeta: location.state?.generalMeta || null,
    };
  }, [examId, location.state, location.search]);

  const {
    initialStudentExamId,
    initialExamData,
    isTopicExam: initialIsTopicExam,
    initialExamMode,
    initialTopicExamId,
    initialGeneralMeta,
  } = getInitialExamState();

  const [currentStudentExamId] = useState<number | null>(initialStudentExamId);
  const [currentExamData] = useState<any | null>(initialExamData);
  const [isTopicExam] = useState<boolean>(initialIsTopicExam);
  const [topicExamId] = useState<number | null>(initialTopicExamId ?? null);
  const [generalMeta] = useState<{ studentId?: number; courseId?: number; attemptNumber?: number } | null>(
    initialGeneralMeta || null
  );
  const [examMode] = useState<string>(initialExamMode || 'standard');
  const isGeneralMode = (examMode || '').toLowerCase() === 'general';
  const [isHomework] = useState<boolean>(location.state?.isHomework || false);

  // Component state
  const [questions, setQuestions] = useState<Question[]>([]);
  const [examInfo, setExamInfo] = useState<ExamInfo | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<number, Answer>>({});
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    // Try to get saved time from localStorage
    const savedTime = localStorage.getItem(`exam_${examId}_time`);
    const savedStartTime = localStorage.getItem(`exam_${examId}_start_time`);

    if (savedTime && savedStartTime) {
      const elapsedTime = Math.floor((Date.now() - parseInt(savedStartTime)) / 1000);
      const remainingTime = Math.max(parseInt(savedTime) - elapsedTime, 0);
      return remainingTime;
    }
    return 60 * 60; // Default 1 hour if no saved time
  });
  const [examCompleted, setExamCompleted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [tabSwitchCount, setTabSwitchCount] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [maxTabSwitches] = useState(1); // Maximum allowed tab switches

  // Part toggle state: 'A' | 'B'
  const [activePart, setActivePart] = useState<'A' | 'B'>('A');

  // Mark for review state: Set of question keys that are marked for review
  const [markedForReview, setMarkedForReview] = useState<Set<string>>(new Set());

  // Track visited questions: Set of question keys that have been visited
  const [visitedQuestions, setVisitedQuestions] = useState<Set<string>>(new Set());

  const hasAutoSubmitBeenCalled = useRef(false); // New ref to prevent double auto-submission


  // Removed unused globalSettings state and calculateMaxMarks helper

  // Helper to get the unique key for a question (prefer question_id, fallback to id)
  const getQuestionKey = useCallback((question: any) => {
    if (question && typeof question.__uid !== 'undefined') {
      return question.__uid;
    }
    const baseId = (typeof question?.question_id !== 'undefined') ? question.question_id : question?.id;
    const part = ((question?.part || 'A').toString().toUpperCase());
    return `${baseId}_${part}`;
  }, []);

  // Helper to determine which part the UI should display this question in.
  // Spec: Questions with part "None" should be displayed under Part B section.
  const getDisplayPart = useCallback((question: any): 'A' | 'B' => {
    const raw = ((question?.part || 'A').toString().toUpperCase());
    if (raw === 'NONE') return 'A';
    return (raw === 'B') ? 'B' : 'A';
  }, []);

  // Place isQuestionAnswered here so it's in scope for render
  const isQuestionAnswered = (question: any): boolean => {
    const key = getQuestionKey(question);
    const answer = answers[key];
    if (!answer) return false;
    if (question.type === 'single_correct_1' || question.type === 'single_correct_A' || question.type === 'single_correct_B') {
      return answer.selectedOptionId !== undefined;
    } else if (question.type === 'multiple_correct') {
      return Array.isArray(answer.selectedOptionIds) && answer.selectedOptionIds.length > 0;
    } else if (question.type === 'integer_type' || question.type === 'numerical_type') {
      return answer.enteredValue !== undefined && answer.enteredValue !== '';
    } else if (question.type === 'three_column_match') {
      const matchesArr: { a: number; b: number | ''; c: number | '' }[] = (answer as any)?.matchingAnswers?.matches || [];
      const qd = (question as any).question_data || {};
      const columnA: string[] = qd.column_a_items || [];
      return matchesArr.length === columnA.length && matchesArr.every((m: { a: number; b: number | ''; c: number | '' }) => m.b !== '' && m.c !== '');
    } else if (question.type === 'matrix_match') {
      const matchesArr: { row_id: number; column_id: number }[] = (answer as any)?.matrixMatches || [];
      return matchesArr.length > 0;
    } else if (question.type === 'comprehension') {
      const qd = (question as any).question_data || {};
      const subQuestions = qd.sub_questions || [];
      const subAnswers = (answer as any)?.subAnswers || {};
      return subQuestions.length > 0 && subQuestions.every((_: any, idx: number) => {
        const ans = subAnswers[idx];
        if (typeof ans === 'number') return true; // single correct
        if (Array.isArray(ans)) return ans.length > 0; // multiple correct
        return ans !== undefined && ans !== '';
      });
    }
    return false;
  };

  const getNormalizedCorrectValue = (question: any): string | number | null => {
    const candidateSources = [
      question?.correct_answer_data,
      question?.question_data?.correct_answer,
      question?.question_data?.answer,
      question?.correct_answer,
    ];

    for (const source of candidateSources) {
      if (source === null || source === undefined) continue;
      if (typeof source === 'object') {
        if (source.answer !== undefined) return source.answer;
        if (source.value !== undefined) return source.value;
      } else {
        return source;
      }
    }

    const fallbackOption = (question?.options || []).find((opt: any) => opt?.is_correct && opt?.content);
    return fallbackOption?.content ?? null;
  };

  const computeGeneralAnswerPayload = useCallback(() => {
    return questions.map((examQuestion: any) => {
      const questionId =
        typeof examQuestion?.question_id !== 'undefined'
          ? examQuestion.question_id
          : examQuestion?.id;
      const questionType = (examQuestion?.type || '').toString().toLowerCase();
      const questionKey = getQuestionKey(examQuestion);
      const recordedAnswer = answers[questionKey];

      if (!recordedAnswer) {
        return { question_id: questionId, answer_data: null };
      }

      let answer_data = null;
      if (
        questionType === 'single_correct_1' ||
        questionType === 'single_correct_a' ||
        questionType === 'single_correct_b'
      ) {
        answer_data = recordedAnswer.selectedOptionId ?? null;
      } else if (questionType === 'multiple_correct') {
        answer_data = recordedAnswer.selectedOptionIds ?? [];
      } else if (questionType === 'integer_type' || questionType === 'numerical_type') {
        answer_data = recordedAnswer.enteredValue ? Number(recordedAnswer.enteredValue) : null;
      } else if (questionType === 'three_column_match') {
        answer_data = {
          matches: (recordedAnswer.matchingAnswers?.matches || []).map((m: any) => ({
            a: typeof m.a === 'number' ? m.a : parseInt(m.a, 10),
            b: m.b !== '' && m.b !== undefined ? parseInt(m.b, 10) : null,
            c: m.c !== '' && m.c !== undefined ? parseInt(m.c, 10) : null,
          }))
        };
      } else if (questionType === 'matrix_match') {
        answer_data = { matches: (recordedAnswer as any)?.matrixMatches || [] };
      } else if (questionType === 'comprehension') {
        const qd = (examQuestion as any).question_data || {};
        const subQuestions = qd.sub_questions || [];
        const subAnswers = (recordedAnswer as any)?.subAnswers || {};
        answer_data = {
          sub_questions: subQuestions.map((sq: any, subIdx: number) => {
            const sqType = sq.type;
            const sqId = sq.id;
            const ans = subAnswers[subIdx];
            if (sqType === 'single_correct_1') {
              return {
                sub_question_id: sqId,
                selected_option_label:
                  Array.isArray(sq.answers) && typeof ans === 'number' && sq.answers[ans]?.option_label
                    ? sq.answers[ans]?.option_label
                    : null,
              };
            } else if (sqType === 'multiple_correct') {
              return {
                sub_question_id: sqId,
                selected_option_labels:
                  Array.isArray(ans) && Array.isArray(sq.answers)
                    ? ans.map((idx: number) => sq.answers[idx]?.option_label).filter(Boolean)
                    : [],
              };
            } else if (sqType === 'integer_type' || sqType === 'numerical_type') {
              return {
                sub_question_id: sqId,
                numerical_answer: ans !== undefined && ans !== '' ? Number(ans) : undefined,
              };
            }
            return { sub_question_id: sqId };
          }),
        };
      }
      return { question_id: questionId, answer_data };
    });
  }, [answers, questions, getQuestionKey]);

  // Modify handleAutoSubmit to use currentStudentExamId
  const submitGeneralExam = useCallback(async () => {
    if (!isTopicExam || !isGeneralMode) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      Alert.error('Oops...', 'Authentication failed. Please log in again.');
      hasAutoSubmitBeenCalled.current = false;
      return;
    }

    if (!topicExamId) {
      Alert.error('Oops...', 'Topic exam ID not found. Cannot submit.');
      hasAutoSubmitBeenCalled.current = false;
      return;
    }

    const studentId = generalMeta?.studentId;
    const courseId = generalMeta?.courseId;

    if (!studentId || !courseId) {
      Alert.error('Oops...', 'Student or course information missing. Please restart the exam.');
      hasAutoSubmitBeenCalled.current = false;
      return;
    }

    const attemptNumber = generalMeta?.attemptNumber ?? 1;

    setIsSubmitting(true);
    try {
      const answerPayload = computeGeneralAnswerPayload();
      const response = await axios.post(
        `${API_URL}/api/topic-general-exams/${topicExamId}/submit`,
        {
          student_id: studentId,
          course_id: courseId,
          attempt_number: attemptNumber,
          answers: answerPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );
      localStorage.removeItem(`exam_${examId}_time`);
      localStorage.removeItem(`exam_${examId}_start_time`);
      localStorage.removeItem(`exam_${examId}_data`);
      setExamCompleted(true);
      navigate(returnTo || '/student/exams', {
        state: {
          examSubmitted: true,
          message: 'Exam submitted successfully!',
          generalResult: response.data,
        },
      });
    } catch (err: any) {
      console.error('General exam submission failed:', err);
      setError(err?.response?.data?.detail || 'Failed to submit exam. Please try again.');
      hasAutoSubmitBeenCalled.current = false;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    computeGeneralAnswerPayload,
    examId,
    generalMeta,
    isGeneralMode,
    isTopicExam,
    navigate,
    topicExamId,
    returnTo,
  ]);

  // Submit homework function
  const submitHomework = useCallback(async () => {
    if (!isTopicExam || !isHomework) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      Alert.error('Oops...', 'Authentication failed. Please log in again.');
      hasAutoSubmitBeenCalled.current = false;
      return;
    }

    if (!topicExamId) {
      Alert.error('Oops...', 'Topic homework ID not found. Cannot submit.');
      hasAutoSubmitBeenCalled.current = false;
      return;
    }

    const studentId = generalMeta?.studentId;
    const courseId = generalMeta?.courseId;

    if (!studentId || !courseId) {
      Alert.error('Oops...', 'Student or course information missing. Please restart the homework.');
      hasAutoSubmitBeenCalled.current = false;
      return;
    }

    const attemptNumber = generalMeta?.attemptNumber ?? 1;

    setIsSubmitting(true);
    try {
      // Compute homework answer payload (same format as general exam)
      const answerPayload = computeGeneralAnswerPayload();
      
      const response = await axios.post(
        `${API_URL}/api/topic-home-works/${topicExamId}/submit`,
        {
          student_id: studentId,
          course_id: courseId,
          attempt_number: attemptNumber,
          answers: answerPayload,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );
      
      localStorage.removeItem(`exam_${examId}_time`);
      localStorage.removeItem(`exam_${examId}_start_time`);
      localStorage.removeItem(`exam_${examId}_data`);
      setExamCompleted(true);
      navigate(returnTo || '/student/exams', {
        state: {
          examSubmitted: true,
          message: 'Homework submitted successfully!',
          homeworkResult: response.data,
        },
      });
    } catch (err: any) {
      console.error('Homework submission failed:', err);
      setError(err?.response?.data?.detail || 'Failed to submit homework. Please try again.');
      hasAutoSubmitBeenCalled.current = false;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    computeGeneralAnswerPayload,
    examId,
    generalMeta,
    isHomework,
    isTopicExam,
    navigate,
    topicExamId,
    returnTo,
  ]);

  const handleAutoSubmit = useCallback(async () => {
    if (hasAutoSubmitBeenCalled.current) {
      console.log('Auto-submit already initiated. Aborting second attempt.');
      return;
    }
    hasAutoSubmitBeenCalled.current = true;
    if (isSubmitting || examCompleted) {
      console.log('Auto-submit already in progress or exam already completed (state check). Aborting.');
      return;
    }
    if (isTopicExam && isHomework) {
      await submitHomework();
      return;
    }
    if (isTopicExam && isGeneralMode) {
      await submitGeneralExam();
      return;
    }
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setShowTimeUpModal(false);
        Alert.error('Oops...', 'Authentication failed. Please log in again.');
        return;
      }
      if (!currentStudentExamId) {
        setShowTimeUpModal(false);
        Alert.error('Oops...', 'Student exam ID not found. Cannot submit.');
        return;
      }
      // Create array of answer submission promises
      const answerSubmissions = questions.map(examQuestion => {
        const key = getQuestionKey(examQuestion);
        const answer = answers[key];
        if (!answer) return null;
        const numericQuestionId = answer?.questionId ?? (examQuestion as any)?.question_id ?? (examQuestion as any)?.id;
        let answerData: any = {
          student_exam_id: currentStudentExamId,
          question_id: numericQuestionId,
        };
        if (examQuestion.type === 'single_correct_1' || examQuestion.type === 'single_correct_A' || examQuestion.type === 'single_correct_B') {
          if (!answer.selectedOptionId) return null;
          answerData.answer_id = answer.selectedOptionId;
        } else if (examQuestion.type === 'multiple_correct') {
          if (!answer.selectedOptionIds || answer.selectedOptionIds.length === 0) return null;
          answerData.selected_options = answer.selectedOptionIds;
        } else if (examQuestion.type === 'integer_type' || examQuestion.type === 'numerical_type') {
          if (!answer.enteredValue || answer.enteredValue === '') return null;
          answerData.numerical_answer = Number(answer.enteredValue);
        } else if (examQuestion.type === 'three_column_match') {
          const matchAns = answer.matchingAnswers;
          if (!matchAns || !Array.isArray(matchAns.matches) || matchAns.matches.length === 0) return null;
          answerData.matching_answers = {
            matches: matchAns.matches.map((m: { a: number; b: number | ''; c: number | '' }) => ({ a: m.a, b: Number(m.b), c: Number(m.c) }))
          };
        } else if (examQuestion.type === 'matrix_match') {
          // Matrix Match: send as matching_answers: { matches: [...] }
          const matchesArr: { row_id: number; column_id: number }[] = (answer as any)?.matrixMatches || [];
          if (!matchesArr.length) return null;
          answerData.matching_answers = { matches: matchesArr };
        } else if (examQuestion.type === 'comprehension') {
          // Comprehension: send as answers: [ ... ]
          const qd = (examQuestion as any).question_data || {};
          const subQuestions = qd.sub_questions || [];
          const subAnswers = (answer as any)?.subAnswers || {};
          answerData.answers = subQuestions.map((sq: any, subIdx: number) => {
            const sqType = sq.type;
            const sqId = sq.id;
            const ans = subAnswers[subIdx];
            if (sqType === 'single_correct_1') {
              // ans is the selected answer index; map to option_label
              const label = Array.isArray(sq.answers) && typeof ans === 'number' && sq.answers[ans]?.option_label;
              return {
                sub_question_id: sqId,
                selected_option_label: label || null
              };
            } else if (sqType === 'multiple_correct') {
              // ans is an array of selected indices; map to option_labels
              const labels = Array.isArray(ans) && Array.isArray(sq.answers)
                ? ans.map((idx: number) => sq.answers[idx]?.option_label).filter(Boolean)
                : [];
              return {
                sub_question_id: sqId,
                selected_option_labels: labels
              };
            } else if (sqType === 'integer_type' || sqType === 'numerical_type') {
              return {
                sub_question_id: sqId,
                numerical_answer: ans !== undefined && ans !== '' ? Number(ans) : undefined
              };
            }
            return { sub_question_id: sqId };
          });
        } else {
          return null;
        }
        return axios.post(
          `${API_URL}/api/student-exams/student-exams/${currentStudentExamId}/answers`,
          answerData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        ).catch(error => {
          console.error('Error submitting answer:', error.response?.data);
          throw error;
        });
      }).filter(Boolean);
      await Promise.all(answerSubmissions);
      console.log('All answers submitted successfully');
      await axios.put(
        `${API_URL}/api/student-exams/${currentStudentExamId}/complete`,
        null,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'accept': 'application/json'
          }
        }
      );
      console.log('Exam marked as completed');
      localStorage.removeItem(`exam_${examId}_time`);
      localStorage.removeItem(`exam_${examId}_start_time`);
      localStorage.removeItem(`exam_${examId}_data`);
      setExamCompleted(true);
      navigate(returnTo || '/student/exams', {
        state: {
          examSubmitted: true,
          message: 'Exam submitted successfully!'
        }
      });
    } catch (error: any) {
      console.error('Submission failed:', error);
      console.error('Error details:', error.response?.data);
      hasAutoSubmitBeenCalled.current = false;
      // The Time's Up modal is a full-screen overlay with its own "Submitting..."
      // spinner and no close button - if a time-up-triggered submit fails, it
      // otherwise stays up forever (nothing else ever clears it), permanently
      // hiding the error message behind it with no way to recover short of a
      // hard refresh.
      setShowTimeUpModal(false);
      setError(
        error.response?.data?.detail
          ? `Submission failed: ${error.response.data.detail}`
          : 'Failed to submit exam. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    examId,
    answers,
    questions,
    navigate,
    setError,
    currentStudentExamId,
    getQuestionKey,
    isSubmitting,
    examCompleted,
    isTopicExam,
    isGeneralMode,
    isHomework,
    submitGeneralExam,
    submitHomework,
    returnTo,
  ]);

  // Handle visibility change
  const handleVisibilityChange = useCallback(async () => {
    // Skip tab switching warnings for homework
    if (isHomework) return;
    
    if (document.hidden) {
      setTabSwitchCount(prev => {
        const newCount = prev + 1;
        // Only trigger auto-submit if exam is not already completed, not already called via ref, AND max switches reached
        if (!examCompleted && !hasAutoSubmitBeenCalled.current && newCount >= maxTabSwitches) {
          console.log('[handleVisibilityChange] Triggering auto-submit due to max tab switches.');
          handleAutoSubmit();
          return prev;
        }
        // Only show warning if exam not completed
        if (!examCompleted) {
          setShowWarning(true);
        }
        return newCount;
      });
    }
  }, [handleAutoSubmit, maxTabSwitches, examCompleted, isHomework]);

  // Removed loading of global settings (unused)

  // Modify timer effect to use handleAutoSubmit and use currentStudentExamId
  useEffect(() => {
    // Skip timer for homework
    if (isHomework) return;
    
    if (examCompleted || isLoading) return; // Prevent timer from running if exam is complete or loading

    // Guard against a stale leftover exam_${examId}_time value driving the
    // timer before this attempt's own data has actually, successfully
    // loaded. fetchExamData has several early-return paths (missing token,
    // missing currentStudentExamId, a failed verify-student-exam call, or
    // the catch-all at the bottom) that flip isLoading to false without
    // ever setting examInfo - without this guard, the effect above would
    // still treat whatever leftover timeRemaining happens to be in state/
    // localStorage as authoritative and fire a bogus auto-submit for an
    // exam that never actually loaded for this attempt.
    if (!examInfo) return;

    // Only auto-submit if time is up, not already submitting, not completed, and not already called via ref
    if (timeRemaining <= 0 && !isSubmitting && !examCompleted && !hasAutoSubmitBeenCalled.current) {
      if (isHomework) return;
      console.log('Timer reached 0, auto-submitting...');
      setShowTimeUpModal(true);
      handleAutoSubmit();
      return;
    }

    if (!localStorage.getItem(`exam_${examId}_start_time`)) {
      localStorage.setItem(`exam_${examId}_start_time`, Date.now().toString());
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          clearInterval(timer);
          handleAutoSubmit();
          return 0;
        }
        localStorage.setItem(`exam_${examId}_time`, newTime.toString());
        return newTime;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [examId, examCompleted, handleAutoSubmit, currentStudentExamId, isLoading, isSubmitting, timeRemaining, isHomework, examInfo]);

  // Update cleanup localStorage when exam is completed (use currentStudentExamId)
  useEffect(() => {
    if (examCompleted && examId) {
      localStorage.removeItem(`exam_${examId}_data`);
      localStorage.removeItem(`exam_${examId}_time`);
      localStorage.removeItem(`exam_${examId}_start_time`);
    }
  }, [examCompleted, examId]);

  // Handle beforeunload event to save state
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!examCompleted) {
        localStorage.setItem(`exam_${examId}_time`, timeRemaining.toString());
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [timeRemaining, examCompleted, examId]);

  // Setup visibility change listener
  useEffect(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  // Prevent page navigation
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };

    // Prevent back button
    const handlePopState = (e: PopStateEvent) => {
      e.preventDefault();
      window.history.pushState(null, '', window.location.pathname);
    };

    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Fetch exam info and questions
  useEffect(() => {
    const fetchExamData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');

        if (!token) {
          Alert.error('Oops...', 'Authentication token not found. Please log in again.');
          setIsLoading(false);
          return;
        }

        // Handle topic exams differently - they come from location.state
        if (isTopicExam && currentExamData) {
          setExamInfo(currentExamData.exam);
          // Topic exams don't have parts, so just map questions directly
          const sorted = (currentExamData.assigned_questions || []).map((q: any, idx: number) => ({
            ...q,
            __uid: `${q.question_id}_TOPIC_${idx}`
          }));
          setQuestions(sorted);
          console.log('Topic exam questions loaded:', sorted);
          setIsLoading(false);
          return;
        }

        // Regular exam flow
        if (!currentStudentExamId) {
          Alert.error('Oops...', 'Student exam ID not found. Please start the exam from the exam list.');
          setIsLoading(false);
          return;
        }

        if (currentExamData) {
          setExamInfo(currentExamData.exam);
          // Order questions by Part: A (or null) first, then B
          const sorted = [...(currentExamData.assigned_questions || [])].sort((q1: any, q2: any) => {
            const p1 = (q1.part || 'A').toString().toUpperCase();
            const p2 = (q2.part || 'A').toString().toUpperCase();
            if (p1 === p2) return 0;
            if (p1 === 'A') return -1;
            if (p2 === 'A') return 1;
            return p1.localeCompare(p2);
          })
            // Attach a unique per-occurrence UID to avoid collisions across parts/positions
            .map((q: any, idx: number) => ({
              ...q,
              __uid: `${typeof q.question_id !== 'undefined' ? q.question_id : q.id}_${((q?.part || 'A').toString().toUpperCase())}_${idx}`
            }));
          setQuestions(sorted);
          console.log('assigned_questions:', currentExamData.assigned_questions);
          setIsLoading(false);
        }

        // First verify the student exam exists and check time window
        try {
          const studentExamResponse = await axios.get(
            `${API_URL}/api/student-exams/${currentStudentExamId}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'accept': 'application/json'
              }
            }
          );

          const now = new Date();
          const startTime = new Date(studentExamResponse.data.exam.start_datetime);
          const endTime = new Date(studentExamResponse.data.exam.end_datetime);
          const studentExamStatus = studentExamResponse.data.status;

          if (now < startTime) {
            Alert.error('Oops...', 'The exam has not started yet. Please wait until the scheduled start time.');
            setIsLoading(false);
            return;
          }

          // Only block if exam is ended and student has not started or already completed
          if (now > endTime && studentExamStatus !== 'in_progress') {
            Alert.error('Oops...', 'The exam has ended. You can no longer take this exam.');
            setIsLoading(false);
            return;
          }
          // If student is in_progress, allow to continue even if now > endTime

        } catch (err) {
          console.error('Error verifying student exam:', err);
          Alert.error('Oops...', 'Failed to verify exam status. Please start the exam from the exam list.');
          setIsLoading(false);
          return;
        }

        // Fetch exam info if not loaded from currentExamData (e.g., initial direct load)
        if (!currentExamData) {
          const examResponse = await axios.get(`${API_URL}/api/exams/exams/${examId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'accept': 'application/json'
            }
          });
          setExamInfo(examResponse.data);
          setTimeRemaining(examResponse.data.duration_minutes * 60);
        }

        // Fetch student profile to get student ID for seeding the random shuffle
        // const profileResponse = await axios.get(`${API_URL}/api/auth/me`, { ... });


      } catch (err: any) {
        console.error('Error fetching exam data:', err);

        if (err.response) {
          if (err.response.status === 401) {
            Alert.error('Oops...', 'Authentication failed. Please log in again.');
          } else if (err.response.status === 403) {
            Alert.error('Oops...', 'You do not have permission to access this exam.');
          } else if (err.response.status === 404) {
            Alert.error('Oops...', 'Exam not found. Please check the exam ID and try again.');
          } else {
            setError(`Failed to load exam data: ${err.response.data?.detail || 'Please try again later.'}`);
          }
        } else {
          Alert.error('Oops...', 'Failed to load exam data. Please check your internet connection and try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    // For topic exams, only need examId and examData
    // For regular exams, need both examId and currentStudentExamId
    if (examId && (isTopicExam ? currentExamData : currentStudentExamId)) {
      fetchExamData();
    }
  }, [examId, currentStudentExamId, currentExamData, isTopicExam]);

  // Add a new useEffect to save exam state to localStorage whenever it changes
  useEffect(() => {
    if ((currentStudentExamId || isTopicExam) && currentExamData && !examCompleted) {
      localStorage.setItem(
        `exam_${examId}_data`,
        JSON.stringify({
          studentExamId: currentStudentExamId,
          examData: currentExamData,
          isTopicExam: isTopicExam,
          examMode,
          topicExamId,
          generalMeta,
        })
      );
    }
  }, [currentStudentExamId, currentExamData, examId, examCompleted, isTopicExam, examMode, topicExamId, generalMeta]);

  // Update timer when exam info is loaded
  useEffect(() => {
    if (examInfo?.duration_minutes) {
      const savedTime = localStorage.getItem(`exam_${examId}_time`);
      if (!savedTime) {
        const initialTime = examInfo.duration_minutes * 60;
        setTimeRemaining(initialTime);
        localStorage.setItem(`exam_${examId}_time`, initialTime.toString());
      }
    }
  }, [examInfo, examId]);

  const currentQuestion = questions[currentQuestionIndex];

  // Time formatter (mm:ss)
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Derived: filter questions by activePart for navigator
  const questionsForPart = React.useMemo(() => {
    return questions.filter((q: any) => getDisplayPart(q) === activePart);
  }, [questions, activePart, getDisplayPart]);

  // Helpers to find last A index and first B index in the global ordered list
  const lastAIndex = React.useMemo(() => {
    let idx = -1;
    questions.forEach((q: any, i: number) => {
      if (getDisplayPart(q) === 'A') idx = i;
    });
    return idx;
  }, [questions, getDisplayPart]);
  const firstBIndex = React.useMemo(() => (
    questions.findIndex((q: any) => getDisplayPart(q) === 'B')
  ), [questions, getDisplayPart]);

  // Count per part for header buttons
  const partCounts = React.useMemo(() => {
    const a = questions.filter((q: any) => getDisplayPart(q) === 'A').length;
    const b = questions.filter((q: any) => getDisplayPart(q) === 'B').length;
    return { A: a, B: b };
  }, [questions, getDisplayPart]);



  // Totals for progress
  const answeredQuestionsCount = Object.keys(answers).length;
  const progressPercentage = (questions.length > 0) ? (answeredQuestionsCount / questions.length) * 100 : 0;

  // Jump to a specific question by global index and sync part
  const jumpToQuestion = (globalIndex: number) => {
    if (globalIndex < 0 || globalIndex >= questions.length) return;
    const target = questions[globalIndex] as any;
    const targetPart = getDisplayPart(target);

    // Mark question as visited
    const qKey = getQuestionKey(target);
    setVisitedQuestions(prev => new Set(prev).add(qKey));

    // Always set the activePart first to match the target question
    setActivePart(targetPart);

    // Set the current question index
    setCurrentQuestionIndex(globalIndex);
  };

  // Update activePart when currentQuestionIndex changes and mark current question as visited
  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex >= 0) {
      const currentQuestion = questions[currentQuestionIndex];
      if (currentQuestion) {
        // Mark current question as visited
        const qKey = getQuestionKey(currentQuestion);
        setVisitedQuestions(prev => new Set(prev).add(qKey));

        const questionPart = getDisplayPart(currentQuestion);
        // Only update if the parts don't match and we're not in the middle of a manual part switch
        if (activePart !== questionPart) {
          setActivePart(questionPart);
        }
      }
    }
  }, [currentQuestionIndex, questions, activePart, getQuestionKey]);

  // Navigation for question pages (now based on filtered list)
  const questionsPerPage = 4;
  const totalPages = Math.ceil(questionsForPart.length / questionsPerPage) || 1;

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      // Move to previous page within the current part and select the first question on that page
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      const partQuestions = questions.filter((q: any) => getDisplayPart(q) === activePart);
      const startIndexInPart = newPage * questionsPerPage;
      const targetInPart = partQuestions[startIndexInPart];
      if (targetInPart) {
        const realIndex = questions.findIndex(q => getQuestionKey(q) === getQuestionKey(targetInPart));
        if (realIndex !== -1) setCurrentQuestionIndex(realIndex);
      }
    } else if (activePart === 'B') {
      // If we're on first page of Part B, switch to last page of Part A and select the last question on that page
      const partAQuestions = questions.filter((q: any) => getDisplayPart(q) === 'A');
      if (partAQuestions.length > 0) {
        setActivePart('A');
        const totalPagesA = Math.ceil(partAQuestions.length / questionsPerPage) || 1;
        const newPage = Math.max(0, totalPagesA - 1);
        setCurrentPage(newPage);
        const startIndexInPart = newPage * questionsPerPage;
        const endIndexInPart = Math.min(startIndexInPart + questionsPerPage, partAQuestions.length) - 1;
        const targetInPart = partAQuestions[endIndexInPart];
        const realIndex = questions.findIndex(q => getQuestionKey(q) === getQuestionKey(targetInPart));
        if (realIndex !== -1) setCurrentQuestionIndex(realIndex);
      }
    }
    // If we're on first page of Part A, do nothing (already at beginning)
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      // Move to next page within the current part and select the first question on that page
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      const partQuestions = questions.filter((q: any) => getDisplayPart(q) === activePart);
      const startIndexInPart = newPage * questionsPerPage;
      const targetInPart = partQuestions[startIndexInPart];
      if (targetInPart) {
        const realIndex = questions.findIndex(q => getQuestionKey(q) === getQuestionKey(targetInPart));
        if (realIndex !== -1) setCurrentQuestionIndex(realIndex);
      }
    } else if (activePart === 'A' && partCounts.B > 0) {
      // If we're on last page of Part A and Part B exists, switch to first page of Part B and select the first question
      const partBQuestions = questions.filter((q: any) => getDisplayPart(q) === 'B');
      if (partBQuestions.length > 0) {
        setActivePart('B');
        setCurrentPage(0);
        const targetInPart = partBQuestions[0];
        const realIndex = questions.findIndex(q => getQuestionKey(q) === getQuestionKey(targetInPart));
        if (realIndex !== -1) setCurrentQuestionIndex(realIndex);
      }
    }
    // If we're on last page of Part B, do nothing (already at end)
  };

  // Get visible questions for current part & page
  const getVisibleQuestions = () => {
    const startIndex = currentPage * questionsPerPage;
    const endIndex = Math.min(startIndex + questionsPerPage, questionsForPart.length);
    return questionsForPart.slice(startIndex, endIndex);
  };

  // Keep the page in sync with the current selected question so it stays visible
  useEffect(() => {
    if (questions.length === 0) return;
    const current = questions[currentQuestionIndex];
    if (!current) return;
    const questionPart = getDisplayPart(current);
    // Ensure activePart matches the question's part
    if (activePart !== questionPart) {
      setActivePart(questionPart);
    }
    // Compute the page index within the active part
    const idxInPart = questionsForPart.findIndex(q => getQuestionKey(q) === getQuestionKey(current));
    if (idxInPart >= 0) {
      const pageForQuestion = Math.floor(idxInPart / questionsPerPage);
      if (pageForQuestion !== currentPage) setCurrentPage(pageForQuestion);
    }
  }, [currentQuestionIndex, questions, questionsForPart, activePart, currentPage, getQuestionKey]);

  // Add new handlers for answer changes:
  const handleSingleCorrectChange = (optionId: number) => {
    setAnswers(prev => ({
      ...prev,
      [getQuestionKey(currentQuestion)]: {
        questionId: (currentQuestion as any)?.question_id ?? (currentQuestion as any)?.id,
        selectedOptionId: optionId,
      }
    }));
  };
  const handleMultipleCorrectChange = (optionId: number) => {
    setAnswers(prev => {
      const prevSelected = prev[getQuestionKey(currentQuestion)]?.selectedOptionIds || [];
      const newSelected = prevSelected.includes(optionId)
        ? prevSelected.filter(id => id !== optionId)
        : [...prevSelected, optionId];
      return {
        ...prev,
        [getQuestionKey(currentQuestion)]: {
          questionId: (currentQuestion as any)?.question_id ?? (currentQuestion as any)?.id,
          selectedOptionIds: newSelected,
        }
      };
    });
  };
  const handleNumericChange = (value: string) => {
    setAnswers(prev => ({
      ...prev,
      [getQuestionKey(currentQuestion)]: {
        questionId: (currentQuestion as any)?.question_id ?? (currentQuestion as any)?.id,
        enteredValue: value,
      }
    }));
  };

  // ---- Mid-exam autosave ----
  // Syncs every answered question to the server as it's answered (debounced),
  // so a crashed/closed browser no longer loses work: the backend expiry sweep
  // grades whatever StudentAnswer rows exist (scheduler.py), so anything saved
  // here is exactly what survives an abandoned attempt and shows up for the
  // admin under Review.
  const lastSavedPayloadRef = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!currentStudentExamId || isTopicExam || examCompleted || isSubmitting) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    const timer = setTimeout(() => {
      Object.entries(answers).forEach(([key, answer]) => {
        if (!answer) return;
        const examQuestion: any = questions.find(q => String(getQuestionKey(q)) === String(key));
        if (!examQuestion) return;
        const numericQuestionId = (answer as any)?.questionId ?? examQuestion?.question_id ?? examQuestion?.id;
        let payload: any = { student_exam_id: currentStudentExamId, question_id: numericQuestionId };
        if (examQuestion.type === 'single_correct_1' || examQuestion.type === 'single_correct_A' || examQuestion.type === 'single_correct_B') {
          if (!(answer as any).selectedOptionId) return;
          payload.answer_id = (answer as any).selectedOptionId;
        } else if (examQuestion.type === 'multiple_correct') {
          const ids = (answer as any).selectedOptionIds;
          if (!ids || ids.length === 0) return;
          payload.selected_options = ids;
        } else if (examQuestion.type === 'integer_type' || examQuestion.type === 'numerical_type') {
          if (!(answer as any).enteredValue) return;
          payload.numerical_answer = Number((answer as any).enteredValue);
        } else if (examQuestion.type === 'three_column_match') {
          const m = (answer as any).matchingAnswers;
          if (!m || !Array.isArray(m.matches) || m.matches.length === 0) return;
          payload.matching_answers = { matches: m.matches.map((x: { a: number; b: number | ''; c: number | '' }) => ({ a: x.a, b: Number(x.b), c: Number(x.c) })) };
        } else if (examQuestion.type === 'matrix_match') {
          const matchesArr: { row_id: number; column_id: number }[] = (answer as any)?.matrixMatches || [];
          if (!matchesArr.length) return;
          payload.matching_answers = { matches: matchesArr };
        } else if (examQuestion.type === 'comprehension') {
          const qd = examQuestion.question_data || {};
          const subQuestions = qd.sub_questions || [];
          const subAnswers = (answer as any)?.subAnswers || {};
          const subs = subQuestions.map((sq: any, subIdx: number) => {
            const ans = subAnswers[subIdx];
            if (sq.type === 'single_correct_1') {
              const label = Array.isArray(sq.answers) && typeof ans === 'number' && sq.answers[ans]?.option_label;
              return { sub_question_id: sq.id, selected_option_label: label || null };
            } else if (sq.type === 'multiple_correct') {
              const labels = Array.isArray(ans) && Array.isArray(sq.answers)
                ? ans.map((idx: number) => sq.answers[idx]?.option_label).filter(Boolean)
                : [];
              return { sub_question_id: sq.id, selected_option_labels: labels };
            } else if (sq.type === 'integer_type' || sq.type === 'numerical_type') {
              return { sub_question_id: sq.id, numerical_answer: ans !== undefined && ans !== '' ? Number(ans) : undefined };
            }
            return { sub_question_id: sq.id };
          });
          const hasAnything = subs.some((s: any) =>
            s.selected_option_label ||
            (s.selected_option_labels && s.selected_option_labels.length > 0) ||
            s.numerical_answer !== undefined);
          if (!hasAnything) return;
          payload.answers = subs;
        } else {
          return;
        }
        const serialized = JSON.stringify(payload);
        if (lastSavedPayloadRef.current[String(key)] === serialized) return;
        axios.post(
          `${API_URL}/api/student-exams/student-exams/${currentStudentExamId}/answers`,
          payload,
          { headers: { Authorization: `Bearer ${token}`, 'accept': 'application/json', 'Content-Type': 'application/json' } }
        ).then(() => {
          lastSavedPayloadRef.current[String(key)] = serialized;
          console.log('Autosaved answer for question', numericQuestionId);
        }).catch(err => {
          console.warn('Autosave failed for question', numericQuestionId, err?.response?.data || err);
        });
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [answers, questions, currentStudentExamId, isTopicExam, examCompleted, isSubmitting, getQuestionKey]);

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      const newIndex = currentQuestionIndex - 1;
      const newQuestion = questions[newIndex] as any;
      const newQuestionPart = ((newQuestion?.part || 'A').toString().toUpperCase()) as 'A' | 'B';

      // Mark question as visited
      const qKey = getQuestionKey(newQuestion);
      setVisitedQuestions(prev => new Set(prev).add(qKey));

      // Update activePart first if switching between parts
      if (activePart !== newQuestionPart) {
        setActivePart(newQuestionPart);
      }

      // Set the current question index
      setCurrentQuestionIndex(newIndex);
    }
  };

  const goToNextQuestion = () => {
    // If the current question is the last in Part A and Part B exists, auto-switch
    if (currentQuestionIndex === lastAIndex && firstBIndex !== -1) {
      const targetQuestion = questions[firstBIndex] as any;
      const qKey = getQuestionKey(targetQuestion);
      setVisitedQuestions(prev => new Set(prev).add(qKey));
      setActivePart('B');
      setCurrentQuestionIndex(firstBIndex);
      return;
    }
    if (currentQuestionIndex < questions.length - 1) {
      const newIndex = currentQuestionIndex + 1;
      const newQuestion = questions[newIndex] as any;
      const newQuestionPart = ((newQuestion?.part || 'A').toString().toUpperCase()) as 'A' | 'B';

      // Mark question as visited
      const qKey = getQuestionKey(newQuestion);
      setVisitedQuestions(prev => new Set(prev).add(qKey));

      // Update activePart first if switching between parts
      if (activePart !== newQuestionPart) {
        setActivePart(newQuestionPart);
      }

      // Set the current question index
      setCurrentQuestionIndex(newIndex);
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout} sidebarContent={null}>
      <div className="font-kumbh">
        {/* Warning Modal */}
        {showWarning && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center font-kumbh">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full font-kumbh">
              <div className="text-red-600 text-xl font-bold mb-4 font-kumbh">Warning!</div>
              <p className="text-gray-600 mb-4 font-kumbh">
                You have switched tabs/windows {tabSwitchCount} time{tabSwitchCount !== 1 ? 's' : ''}.
                Maximum allowed switches: {maxTabSwitches}.
                Your exam will be automatically submitted if you exceed this limit.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowWarning(false)}
                  className="px-4 py-2 bg-[#111827] text-white rounded hover:bg-gray-800 font-kumbh"
                >
                  I Understand
                </button>
              </div>
            </div>
          </div>
        )}
        {error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6 font-kumbh" role="alert">
            <p className="font-kumbh">{error}</p>
          </div>
        ) : isLoading ? (
          <div className="flex justify-center items-center h-64 font-kumbh">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 font-kumbh"></div>
          </div>
        ) : examCompleted ? (
          <div className="text-center py-8 font-kumbh">
            <div className="bg-green-100 text-green-800 p-4 rounded-lg mb-6 font-kumbh">
              <h2 className="text-xl font-semibold font-kumbh">Exam Submitted Successfully</h2>
              <p className="mt-2 font-kumbh">Your answers have been recorded.</p>
            </div>
            <Button
              as={Link}
              to={`/student/exams/${examId}/results`}
              variant="primary"
              className="font-kumbh"
            >
              View Results
            </Button>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-8 font-kumbh">
            <p className="text-gray-600 font-kumbh">No questions available for this exam.</p>
          </div>
        ) : (
          <div>
            {/* Exam header */}
            <div className="flex justify-between items-center mb-6 font-kumbh">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-kumbh">{examInfo?.title}</h1>
                <p className="text-gray-600 mt-1 font-kumbh">{examInfo?.description}</p>
                <p className="text-sm text-gray-600 mt-1 font-kumbh">
                  You will be answering {questions.length} {questions.length === 1 ? 'question' : 'questions'} selected for this {isHomework ? 'homework' : 'exam'}.
                </p>
              </div>
              <div className="flex flex-row md:flex-col items-center md:items-end justify-between w-full md:w-auto mt-4 md:mt-0 font-kumbh gap-4 md:gap-0">
                <div className="flex flex-col items-start md:items-end">
                  {!isHomework && (
                    <div className="text-xl font-bold text-red-600  md:mb-2 font-kumbh">
                      {formatTime(timeRemaining)}
                    </div>
                  )}
                  <div className="text-sm text-gray-600 font-kumbh">
                    Question {currentQuestionIndex + 1} of {questions.length}
                  </div>
                </div>
                <Button
                  variant="primary"
                  onClick={() => setShowSubmitConfirm(true)}
                  disabled={isSubmitting}
                  className="bg-green-600 text-white font-semibold py-2 px-6 rounded-lg shadow border border-green-700 hover:bg-green-700 focus:ring-2 focus:ring-green-400 transition duration-200 whitespace-nowrap"
                >
                  {isSubmitting ? 'Submitting...' : isHomework ? 'Submit Homework' : 'Submit Exam'}
                </Button>
              </div>
            </div>

            {/* Global style to constrain embedded images inside question text so they don't break the layout */}
            <style>{`
              .exam-content img {
                max-width: 100% !important;
                height: auto !important;
              }
            `}</style>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-6 font-kumbh">
              <div
                className="bg-[#111827] h-2.5 rounded-full font-kumbh"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 font-kumbh items-start">
              {/* Question content */}
              <div className="lg:col-span-3 font-kumbh min-w-0">
                <Card>
                  <div className="p-6 font-kumbh">
                    <div className="flex justify-between mb-6 font-kumbh">
                      <div>
                        <span className="text-sm font-medium text-gray-600 font-kumbh">Question {currentQuestionIndex + 1}</span>
                        <span className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded font-kumbh">
                          {currentQuestion?.marks} {currentQuestion?.marks === 1 ? 'mark' : 'marks'}
                        </span>
                        {/* Show question type badge */}
                        <span className="ml-2 bg-gray-100 text-gray-900 text-xs font-medium px-2.5 py-0.5 rounded font-kumbh">
                          {(() => {
                            switch (currentQuestion.type) {
                              case 'single_correct_1':
                              case 'single_correct_A':
                              case 'single_correct_B':
                                return 'Single Correct';
                              case 'multiple_correct': return 'Multiple Correct';
                              case 'integer_type': return 'Integer Type';
                              case 'numerical_type': return 'Numerical Type';
                              case 'three_column_match': return 'Three Column Match';
                              case 'matrix_match': return 'Matrix Match';
                              case 'comprehension': return 'Comprehension';
                              default: return currentQuestion.type;
                            }
                          })()}
                        </span>
                        {/* Part badge */}
                        <span className="ml-2 bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded font-kumbh">
                          Part {getDisplayPart(currentQuestion)}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 font-kumbh">
                        {/* Show type for clarity */}
                        {(() => {
                          switch (currentQuestion.type) {
                            case 'single_correct_1':
                            case 'single_correct_A':
                            case 'single_correct_B':
                              return 'MCQ';
                            case 'multiple_correct': return 'MCQ (Multiple)';
                            case 'integer_type': return 'Integer';
                            case 'numerical_type': return 'Numerical';
                            case 'three_column_match': return 'Three Column Match';
                            case 'matrix_match': return 'Matrix Match';
                            case 'comprehension': return 'Comprehension';
                            default: return '';
                          }
                        })()}
                      </div>
                    </div>

                    {currentQuestion ? (
                      <>
                        <h2 className="text-lg font-semibold text-gray-900 mb-6 font-kumbh exam-content">
                          <div dangerouslySetInnerHTML={{ __html: currentQuestion.content ? renderMathHtml(currentQuestion.content) : 'Question content not available' }} />
                        </h2>
                        {/* Note removed intentionally */}
                        {/* Display image if available */}
                        {currentQuestion.image_url && (
                          <div className="mb-6 flex justify-center items-center bg-gray-50 border rounded-lg h-64 p-2 w-full">
                            {currentQuestion.image_url.toLowerCase().endsWith('.pdf') ? (
                              <iframe
                                src={currentQuestion.image_url}
                                className="w-full h-full rounded"
                                title="Question PDF"
                              />
                            ) : (
                              <img
                                src={currentQuestion.image_url}
                                alt="Question"
                                className="max-h-full max-w-full object-contain mx-auto"
                              />
                            )}
                          </div>
                        )}
                        <div className="space-y-3 font-kumbh">
                          {/* Render answer UI by type */}
                          {(() => {
                            if (currentQuestion.type === 'single_correct_1' || currentQuestion.type === 'single_correct_A' || currentQuestion.type === 'single_correct_B') {
                              const allOptionsHaveImages = (currentQuestion.options || []).every((option: ApiOption) => !!option.image_url);
                              if (allOptionsHaveImages) {
                                // Image-based MCQ: 2-column grid, large images
                                return (
                                  <div className="grid grid-cols-2 gap-6">
                                    {(currentQuestion.options || []).map((option: ApiOption) => {
                                      const isSelected = answers[getQuestionKey(currentQuestion)]?.selectedOptionId === option.id;
                                      return (
                                        <label
                                          key={option.id}
                                          htmlFor={`option-${option.id}`}
                                          className={`flex flex-col items-center cursor-pointer border rounded-lg p-3 transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200 hover:border-blue-300'}`}
                                        >
                                          <input
                                            id={`option-${option.id}`}
                                            type="radio"
                                            name={`question-${getQuestionKey(currentQuestion)}`}
                                            value={option.id}
                                            checked={isSelected}
                                            onChange={() => handleSingleCorrectChange(option.id)}
                                            className="hidden"
                                          />
                                          <div 
                                            className="w-full h-48 flex justify-center items-center bg-white rounded mb-2 p-1"
                                            style={{ border: isSelected ? '2px solid #2563eb' : '1px solid #e5e7eb' }}
                                          >
                                            {option.image_url!.toLowerCase().endsWith('.pdf') ? (
                                              <iframe src={option.image_url!} className="w-full h-full pointer-events-none" title="Option" />
                                            ) : (
                                              <img src={option.image_url!} alt="Option" className="max-w-full max-h-full object-contain" />
                                            )}
                                          </div>
                                          <span className="text-center text-sm text-gray-600 font-kumbh mt-1">
                                            <div dangerouslySetInnerHTML={{ __html: renderMathHtml(option.content) }} />
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                );
                              } else {
                                // Normal MCQ: radio + text + (larger) image if present
                                return (currentQuestion.options || []).map((option: ApiOption) => (
                                  <div key={option.id} className="flex items-start">
                                    <div className="flex items-center h-5">
                                      <input
                                        id={`option-${option.id}`}
                                        type="radio"
                                        name={`question-${getQuestionKey(currentQuestion)}`}
                                        value={option.id}
                                        checked={answers[getQuestionKey(currentQuestion)]?.selectedOptionId === option.id}
                                        onChange={() => handleSingleCorrectChange(option.id)}
                                        className="w-4 h-4 text-[#111827] border-gray-300 focus:ring-gray-700"
                                      />
                                    </div>
                                    <label htmlFor={`option-${option.id}`} className="ml-3 text-sm font-medium text-gray-600 font-kumbh flex items-center gap-2">
                                      {option.image_url && (
                                        <img
                                          src={option.image_url}
                                          alt={`Option ${option.content || option.id}`}
                                          className="inline-block h-14 w-14 object-contain border rounded mr-2"
                                          style={{ maxWidth: 60, maxHeight: 60 }}
                                        />
                                      )}
                                      <div dangerouslySetInnerHTML={{ __html: renderMathHtml(option.content) }} />
                                    </label>
                                  </div>
                                ));
                              }
                            } else if (currentQuestion.type === 'multiple_correct') {
                              const allOptionsHaveImagesMC = (currentQuestion.options || []).every((option: ApiOption) => !!option.image_url);
                              if (allOptionsHaveImagesMC) {
                                // Image-based MCQ: 2-column grid, large images
                                return (
                                  <div className="grid grid-cols-2 gap-6">
                                    {(currentQuestion.options || []).map((option: ApiOption) => {
                                      const isSelected = answers[getQuestionKey(currentQuestion)]?.selectedOptionIds?.includes(option.id) || false;
                                      return (
                                        <label
                                          key={option.id}
                                          htmlFor={`option-mc-${option.id}`}
                                          className={`flex flex-col items-center cursor-pointer border rounded-lg p-3 transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-300' : 'border-gray-200 hover:border-blue-300'}`}
                                        >
                                          <input
                                            id={`option-mc-${option.id}`}
                                            type="checkbox"
                                            name={`question-${getQuestionKey(currentQuestion)}`}
                                            value={option.id}
                                            checked={isSelected}
                                            onChange={() => handleMultipleCorrectChange(option.id)}
                                            className="hidden"
                                          />
                                          <img
                                            src={option.image_url!}
                                            alt={`Option ${option.content || option.id}`}
                                            className="object-contain rounded mb-2"
                                            style={{ width: 120, height: 120, border: isSelected ? '2px solid #2563eb' : '1px solid #e5e7eb' }}
                                          />
                                          <span className="text-center text-sm text-gray-600 font-kumbh mt-1">
                                            <div dangerouslySetInnerHTML={{ __html: renderMathHtml(option.content) }} />
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                );
                              } else {
                                // Normal MCQ: checkbox + text + (larger) image if present
                                return (currentQuestion.options || []).map((option: ApiOption) => (
                                  <div key={option.id} className="flex items-start">
                                    <div className="flex items-center h-5">
                                      <input
                                        id={`option-mc-${option.id}`}
                                        type="checkbox"
                                        name={`question-${getQuestionKey(currentQuestion)}`}
                                        value={option.id}
                                        checked={answers[getQuestionKey(currentQuestion)]?.selectedOptionIds?.includes(option.id) || false}
                                        onChange={() => handleMultipleCorrectChange(option.id)}
                                        className="w-4 h-4 text-[#111827] border-gray-300 focus:ring-gray-700"
                                      />
                                    </div>
                                    <label htmlFor={`option-mc-${option.id}`} className="ml-3 text-sm font-medium text-gray-600 font-kumbh flex items-center gap-2">
                                      {option.image_url && (
                                        <img
                                          src={option.image_url}
                                          alt={`Option ${option.content || option.id}`}
                                          className="inline-block h-14 w-14 object-contain border rounded mr-2"
                                          style={{ maxWidth: 60, maxHeight: 60 }}
                                        />
                                      )}
                                      <div dangerouslySetInnerHTML={{ __html: renderMathHtml(option.content) }} />
                                    </label>
                                  </div>
                                ));
                              }
                            } else if (currentQuestion.type === 'integer_type' || currentQuestion.type === 'numerical_type') {
                              // Integer/Numerical (numeric input)
                              return (
                                <div className="flex flex-col max-w-xs">
                                  <label htmlFor={`numeric-answer-${getQuestionKey(currentQuestion)}`} className="mb-2 text-sm text-gray-600 font-kumbh">
                                    Enter your answer:
                                  </label>
                                  <input
                                    id={`numeric-answer-${getQuestionKey(currentQuestion)}`}
                                    type="number"
                                    value={answers[getQuestionKey(currentQuestion)]?.enteredValue || ''}
                                    onChange={e => handleNumericChange(e.target.value)}
                                    className="border rounded px-2 py-1"
                                    step={currentQuestion.type === 'numerical_type' ? 'any' : '1'}
                                  />
                                </div>
                              );
                            } else if (currentQuestion.type === 'three_column_match') {
                              // Render a table for three column match
                              const qd = (currentQuestion as any).question_data || {};
                              const columnA = qd.column_a_items || [];
                              const columnB = qd.column_b_items || [];
                              const columnC = qd.column_c_items || [];
                              // Get current matches from state
                              const matchesArr: { a: number; b: number | ''; c: number | '' }[] = (answers[getQuestionKey(currentQuestion)] as Answer)?.matchingAnswers?.matches || columnA.map((_: string, idx: number) => ({ a: idx, b: '', c: '' }));
                              const handleMatchChange = (rowIdx: number, col: 'b' | 'c', value: string) => {
                                setAnswers(prev => {
                                  const prevMatches: { a: number; b: number | ''; c: number | '' }[] = (prev[getQuestionKey(currentQuestion)] as Answer)?.matchingAnswers?.matches || columnA.map((_: string, idx: number) => ({ a: idx, b: '', c: '' }));
                                  const newMatches = prevMatches.map((m: { a: number; b: number | ''; c: number | '' }, i: number) => i === rowIdx ? { ...m, [col]: value } : m);
                                  return {
                                    ...prev,
                                    [getQuestionKey(currentQuestion)]: {
                                      questionId: (currentQuestion as any)?.question_id ?? (currentQuestion as any)?.id,
                                      matchingAnswers: { matches: newMatches }
                                    }
                                  };
                                });
                              };
                              return (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full border mb-2">
                                    <thead>
                                      <tr>
                                        <th className="px-2 py-1 border">Column A</th>
                                        <th className="px-2 py-1 border">Column B</th>
                                        <th className="px-2 py-1 border">Column C</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {columnA.map((a: string, idx: number) => (
                                        <tr key={idx}>
                                          <td className="border px-2 py-1">
                                            <div dangerouslySetInnerHTML={{ __html: renderMathHtml(a) }} />
                                          </td>
                                          <td className="border px-2 py-1">
                                            <select
                                              value={matchesArr[idx]?.b ?? ''}
                                              onChange={e => handleMatchChange(idx, 'b', e.target.value)}
                                              className="border rounded px-2 py-1"
                                            >
                                              <option value="">Select</option>
                                              {columnB.map((b: string, bIdx: number) => (
                                                <option key={bIdx} value={bIdx}>{b}</option>
                                              ))}
                                            </select>
                                          </td>
                                          <td className="border px-2 py-1">
                                            <select
                                              value={matchesArr[idx]?.c ?? ''}
                                              onChange={e => handleMatchChange(idx, 'c', e.target.value)}
                                              className="border rounded px-2 py-1"
                                            >
                                              <option value="">Select</option>
                                              {columnC.map((c: string, cIdx: number) => (
                                                <option key={cIdx} value={cIdx}>{c}</option>
                                              ))}
                                            </select>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <p className="text-xs text-gray-600">For each row, select the matching B and C for the given A.</p>
                                </div>
                              );
                            } else if (currentQuestion.type === 'matrix_match') {
                              // Matrix Match: render a table with checkboxes for each cell
                              const qd = (currentQuestion as any).question_data || {};
                              const rows = qd.rows || [];
                              const columns = qd.columns || [];
                              // Get current matches from state
                              const matchesArr: { row_id: number; column_id: number }[] = (answers[getQuestionKey(currentQuestion)] as any)?.matrixMatches || [];
                              const isChecked = (rowId: number, colId: number) => matchesArr.some((m: any) => m.row_id === rowId && m.column_id === colId);
                              const handleMatrixCheck = (rowId: number, colId: number) => {
                                setAnswers(prev => {
                                  const prevMatches: { row_id: number; column_id: number }[] = (prev[getQuestionKey(currentQuestion)] as any)?.matrixMatches || [];
                                  const exists = prevMatches.some(m => m.row_id === rowId && m.column_id === colId);
                                  const newMatches = exists
                                    ? prevMatches.filter(m => !(m.row_id === rowId && m.column_id === colId))
                                    : [...prevMatches, { row_id: rowId, column_id: colId }];
                                  return {
                                    ...prev,
                                    [getQuestionKey(currentQuestion)]: {
                                      questionId: (currentQuestion as any)?.question_id ?? (currentQuestion as any)?.id,
                                      matrixMatches: newMatches
                                    }
                                  };
                                });
                              };
                              return (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full border mb-2">
                                    <thead>
                                      <tr>
                                        <th className="px-2 py-1 border"></th>
                                        {columns.map((col: any, colIdx: number) => (
                                          <th key={colIdx} className="px-2 py-1 border">
                                            <div dangerouslySetInnerHTML={{ __html: renderMathHtml(typeof col === 'object' ? col.text : col) }} />
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {rows.map((row: any, rowIdx: number) => (
                                        <tr key={rowIdx}>
                                          <th className="px-2 py-1 border text-left font-normal">
                                            <div dangerouslySetInnerHTML={{ __html: renderMathHtml(typeof row === 'object' ? row.text : row) }} />
                                          </th>
                                          {columns.map((col: any, colIdx: number) => (
                                            <td key={colIdx} className="px-2 py-1 border text-center">
                                              <input
                                                type="checkbox"
                                                checked={isChecked(row.id, col.id)}
                                                onChange={() => handleMatrixCheck(row.id, col.id)}
                                              />
                                            </td>
                                          ))}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <p className="text-xs text-gray-600">Check all correct matches for each row and column.</p>
                                </div>
                              );
                            } else if (currentQuestion.type === 'comprehension') {
                              // Comprehension: render paragraph and sub-questions
                              const qd = (currentQuestion as any).question_data || {};
                              const subQuestions = qd.sub_questions || [];
                              // Store sub-question answers in answers state under parent question
                              const subAnswers = (answers[getQuestionKey(currentQuestion)] as any)?.subAnswers || {};
                              const handleSubAnswerChange = (subIdx: number, value: any) => {
                                setAnswers(prev => {
                                  const prevSub = (prev[getQuestionKey(currentQuestion)] as any)?.subAnswers || {};
                                  return {
                                    ...prev,
                                    [getQuestionKey(currentQuestion)]: {
                                      questionId: (currentQuestion as any)?.question_id ?? (currentQuestion as any)?.id,
                                      subAnswers: { ...prevSub, [subIdx]: value }
                                    }
                                  };
                                });
                              };
                              return (
                                <div>
                                  <div className="mb-4 p-3 bg-gray-50 rounded">
                                    <div className="font-semibold mb-2">Comprehension Paragraph</div>
                                    <div className="text-gray-900" dangerouslySetInnerHTML={{ __html: renderMathHtml(currentQuestion.content) }} />
                                  </div>
                                  <div className="space-y-6">
                                    {subQuestions.map((sq: any, subIdx: number) => (
                                      <div key={subIdx} className="p-3 border rounded">
                                        <div className="font-medium mb-2">
                                          Q{subIdx + 1}: <div dangerouslySetInnerHTML={{ __html: renderMathHtml(sq.content) }} />
                                        </div>
                                        {/* Render answer UI for sub-question type */}
                                        {sq.type === 'single_correct_1' && (
                                          <div className="space-y-2">
                                            {sq.answers.map((ans: any, aidx: number) => (
                                              <label key={aidx} className="flex items-center gap-2">
                                                <input
                                                  type="radio"
                                                  name={`subq-${getQuestionKey(currentQuestion)}-${subIdx}`}
                                                  checked={subAnswers[subIdx] === aidx}
                                                  onChange={() => handleSubAnswerChange(subIdx, aidx)}
                                                />
                                                <span>
                                                  {ans.option_label}. <div dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} />
                                                </span>
                                              </label>
                                            ))}
                                          </div>
                                        )}
                                        {sq.type === 'multiple_correct' && (
                                          <div className="space-y-2">
                                            {sq.answers.map((ans: any, aidx: number) => (
                                              <label key={aidx} className="flex items-center gap-2">
                                                <input
                                                  type="checkbox"
                                                  checked={Array.isArray(subAnswers[subIdx]) && subAnswers[subIdx].includes(aidx)}
                                                  onChange={() => {
                                                    const prev = Array.isArray(subAnswers[subIdx]) ? subAnswers[subIdx] : [];
                                                    handleSubAnswerChange(subIdx, prev.includes(aidx) ? prev.filter((v: number) => v !== aidx) : [...prev, aidx]);
                                                  }}
                                                />
                                                <span>
                                                  {ans.option_label}. <div dangerouslySetInnerHTML={{ __html: renderMathHtml(ans.content) }} />
                                                </span>
                                              </label>
                                            ))}
                                          </div>
                                        )}
                                        {(sq.type === 'integer_type' || sq.type === 'numerical_type') && (
                                          <div className="flex flex-col max-w-xs">
                                            <input
                                              type="number"
                                              value={subAnswers[subIdx] || ''}
                                              onChange={e => handleSubAnswerChange(subIdx, e.target.value)}
                                              className="border rounded px-2 py-1"
                                            />
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            } else {
                              return <div>Unsupported question type</div>;
                            }
                          })()}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-gray-600 font-kumbh">
                        No questions available
                      </div>
                    )}

                    {/* Navigation buttons */}
                    <div className="flex justify-between items-center mt-8">
                      <Button
                        variant="secondary"
                        onClick={goToPreviousQuestion}
                        disabled={currentQuestionIndex === 0}
                      >
                        Previous
                      </Button>
                      {currentQuestion && (
                        <div className="flex items-center gap-2" style={{ marginLeft: '535px' }}>
                          <label className={`flex items-center gap-2 ${isQuestionAnswered(currentQuestion) ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}>
                            <input
                              type="checkbox"
                              checked={markedForReview.has(getQuestionKey(currentQuestion))}
                              disabled={!isQuestionAnswered(currentQuestion)}
                              onChange={(e) => {
                                const qKey = getQuestionKey(currentQuestion);
                                setMarkedForReview(prev => {
                                  const newSet = new Set(prev);
                                  if (e.target.checked) {
                                    newSet.add(qKey);
                                  } else {
                                    newSet.delete(qKey);
                                  }
                                  return newSet;
                                });
                              }}
                              className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 disabled:cursor-not-allowed"
                            />
                            <span className="text-sm text-gray-600 font-kumbh">Mark as review</span>
                          </label>
                        </div>
                      )}
                      <Button
                        variant="secondary"
                        onClick={goToNextQuestion}
                        disabled={currentQuestionIndex === questions.length - 1}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Question navigation sidebar */}
              <div className="lg:col-span-1 font-kumbh">
                <Card>
                  <div className="p-4 font-kumbh">
                    {/* Part Toggle */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="inline-flex rounded overflow-hidden border">
                        <button
                          onClick={() => {
                            // Find the first question of Part A and navigate to it
                            const firstPartAIndex = questions.findIndex((q: any) => ((q?.part || 'A').toString().toUpperCase()) === 'A');
                            if (firstPartAIndex !== -1) {
                              setActivePart('A');
                              setCurrentQuestionIndex(firstPartAIndex);
                              setCurrentPage(0);
                            }
                          }}
                          className={`${activePart === 'A' ? 'bg-[#111827] text-white' : 'bg-gray-100 text-gray-900'} px-3 py-1 text-sm font-medium`}
                        >
                          Part A ({partCounts.A})
                        </button>
                        <button
                          onClick={() => {
                            // Find the first question of Part B and navigate to it
                            const firstPartBIndex = questions.findIndex((q: any) => ((q?.part || 'A').toString().toUpperCase()) === 'B');
                            if (firstPartBIndex !== -1) {
                              setActivePart('B');
                              setCurrentQuestionIndex(firstPartBIndex);
                              setCurrentPage(0);
                            }
                          }}
                          className={`${activePart === 'B' ? 'bg-[#111827] text-white' : 'bg-gray-100 text-gray-900'} px-3 py-1 text-sm font-medium border-l`}
                        >
                          Part B ({partCounts.B})
                        </button>
                      </div>
                    </div>

                    <h3 className="font-semibold text-gray-900 mb-4 font-kumbh">Question Navigator</h3>

                    {/* Display all questions for the active part */}
                    <div className="max-h-96 overflow-y-auto mb-4">
                      <div className="grid grid-cols-4 gap-2">
                        {questionsForPart.map((question, partIndex) => {
                          // Derive a stable real index by matching both key and part
                          const qKey = getQuestionKey(question as any);
                          const qPart = (((question as any)?.part || 'A').toString().toUpperCase());
                          const realIndex = questions.findIndex((q: any) => (
                            getQuestionKey(q) === qKey && (((q?.part || 'A').toString().toUpperCase()) === qPart)
                          ));

                          // Calculate the question number within the active part (1-based)
                          const questionNumberInPart = partIndex + 1;

                          // Determine question state for color coding
                          const isCurrent = realIndex === currentQuestionIndex;
                          const isVisited = visitedQuestions.has(qKey);
                          const isAnswered = isQuestionAnswered(question);
                          const isMarkedForReview = markedForReview.has(qKey);
                          const isSkipped = isVisited && !isAnswered;

                          // Color logic based on priority:
                          // 1. Current Question: Blue (highest priority)
                          // 2. Visited & Answered with review: Orange
                          // 3. Visited & Answered: Green
                          // 4. Visited & Skipped: Red
                          // 5. Not Visited: Grey
                          let buttonClass = '';
                          if (isCurrent) {
                            buttonClass = 'bg-[#111827] text-white ring-2 ring-blue-300';
                          } else if (isVisited && isAnswered && isMarkedForReview) {
                            buttonClass = 'bg-orange-200 text-orange-900 border-2 border-orange-500 hover:bg-orange-300';
                          } else if (isVisited && isAnswered) {
                            buttonClass = 'bg-green-100 text-green-800 border border-green-300 hover:bg-green-200';
                          } else if (isSkipped) {
                            buttonClass = 'bg-red-100 text-red-800 border-2 border-red-400 hover:bg-red-200';
                          } else {
                            buttonClass = 'bg-gray-100 text-gray-900 hover:bg-gray-200 border border-gray-300';
                          }

                          return (
                            <button
                              key={`${qKey}_${qPart}_${realIndex}`}
                              onClick={() => jumpToQuestion(realIndex)}
                              className={`w-full aspect-square rounded flex items-center justify-center text-sm font-medium transition-colors ${buttonClass}`}
                              title={`Question ${questionNumberInPart} of Part ${activePart}${isMarkedForReview ? ' (Marked for review)' : ''}${isSkipped ? ' (Skipped)' : ''}`}
                            >
                              {questionNumberInPart}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-4 space-y-2 font-kumbh">
                      <div className="flex items-center font-kumbh">
                        <div className="w-4 h-4 bg-gray-100 mr-2 border border-gray-300"></div>
                        <span className="text-sm text-gray-600 font-kumbh">Not Visited</span>
                      </div>
                      <div className="flex items-center font-kumbh">
                        <div className="w-4 h-4 bg-green-100 border border-green-300 mr-2"></div>
                        <span className="text-sm text-gray-600 font-kumbh">Visited & Answered</span>
                      </div>
                      <div className="flex items-center font-kumbh">
                        <div className="w-4 h-4 bg-orange-200 border-2 border-orange-500 mr-2"></div>
                        <span className="text-sm text-gray-600 font-kumbh">Visited & Answered (Review)</span>
                      </div>
                      <div className="flex items-center font-kumbh">
                        <div className="w-4 h-4 bg-red-100 border-2 border-red-400 mr-2"></div>
                        <span className="text-sm text-gray-600 font-kumbh">Visited & Skipped</span>
                      </div>
                      <div className="flex items-center font-kumbh">
                        <div className="w-4 h-4 bg-[#111827] mr-2"></div>
                        <span className="text-sm text-gray-600 font-kumbh">Current Question</span>
                      </div>
                    </div>

                    <div className="mt-6 font-kumbh">
                      <p className="text-sm text-gray-600 mb-2 font-kumbh">
                        Answered: {answeredQuestionsCount} of {questions.length}
                      </p>
                    </div>


                  </div>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>

      {showTimeUpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-80">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2 font-kumbh">
              Time's Up
            </h3>
            <p className="text-gray-600 mb-6 font-kumbh">
              Your exam time has ended. Your answers have been saved and the exam will be submitted automatically.
            </p>
            <div className="flex justify-center items-center space-x-2 text-[#111827] font-kumbh">
              <svg className="animate-spin h-5 w-5 text-[#111827]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="font-medium">Submitting...</span>
            </div>
          </div>
        </div>
      )}

      {showSubmitConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4 font-kumbh">
              {isHomework ? 'Submit Homework' : 'Submit Exam'}
            </h3>
            <p className="text-gray-600 mb-4 font-kumbh">
              Are you sure you want to submit? You won't be able to change your answers after submission.
            </p>
            {questions.length - answeredQuestionsCount > 0 && (
              <div className="mb-6 p-3 bg-yellow-50 text-yellow-800 rounded-md border border-yellow-200 font-kumbh">
                <strong>Warning:</strong> You have {questions.length - answeredQuestionsCount} unanswered {questions.length - answeredQuestionsCount === 1 ? 'question' : 'questions'}.
              </div>
            )}
            <div className="flex justify-end space-x-3 font-kumbh">
              <Button
                variant="secondary"
                onClick={() => setShowSubmitConfirm(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  setShowSubmitConfirm(false);
                  handleAutoSubmit();
                }}
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? 'Submitting...' : 'Yes, Submit'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default ExamTaking;
